import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ICON_TILE,
  LIST_CARD,
  PAGE_COLUMN,
  PageHeader,
  SECTION_TITLE,
} from "@/components/ui/redesign/page";
import { cn, container } from "@/lib/utils";
import { useMediaQuery } from "@/pages/Visit/workspace/layout";

export interface SettingsSection {
  value: string;
  label: string;
  description: string;
  icon: ReactNode;
  content: ReactNode;
}

const NAV_ITEM =
  "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-v3-text-3 transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring aria-[current=page]:bg-v3-surface-3 aria-[current=page]:font-semibold aria-[current=page]:text-v3-text lg:min-h-10 [&_svg]:size-[18px] [&_svg]:shrink-0";

const SectionPanel = ({
  section,
}: {
  section: SettingsSection;
}): JSX.Element => (
  <section
    aria-labelledby={`settings-${section.value}`}
    className={cn(LIST_CARD, "px-4 py-5 sm:px-6 sm:py-6")}
    data-testid={`settings-panel-${section.value}`}
  >
    <header className="mb-5 border-b border-v3-border pb-4">
      <h2 className={SECTION_TITLE} id={`settings-${section.value}`}>
        {section.label}
      </h2>
      <p className="mt-1 text-sm text-v3-text-3">{section.description}</p>
    </header>
    {/* Touch screens: the sections' own edit and action buttons get 44px
        targets without changing the section components. */}
    <div className="max-lg:[&_button:not([role=switch])]:min-h-11 max-lg:[&_button:not([role=switch])]:min-w-11">
      {section.content}
    </div>
  </section>
);

// Settings: from 1024px a section list on the left and the open section on
// the right; on phones and tablets a list of sections that opens one at a
// time, with a way back (like iOS Settings). The open section is the URL
// hash, as with the classic tabs, so links like /settings#emr-launch work.
export const SettingsHome = ({
  sections,
  summary,
}: {
  sections: SettingsSection[];
  // Plan summary under the title.
  summary?: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const wide = useMediaQuery("(min-width: 1024px)");
  const hash = useRouterState({
    select: (state) => state.location.hash.replace("#", "").trim(),
  });
  const requested = sections.find((section) => section.value === hash);
  const active = requested ?? (wide ? sections[0] : undefined);

  const header = <PageHeader summary={summary} title={t("settings.title")} />;

  if (!wide && active) {
    return (
      <>
        <PageHeader title={t("settings.title")} />
        <motion.div
          key={active.value}
          animate="show"
          className={cn(PAGE_COLUMN, "space-y-4")}
          data-testid="settings-home"
          initial="hidden"
          variants={container}
        >
          <Link
            className="-ml-2 inline-flex h-11 items-center gap-1 rounded-full px-2 text-sm font-medium text-v3-text-2 hover:text-v3-text hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            data-testid="settings-back"
            hash=""
            to="."
          >
            <ChevronLeft aria-hidden className="size-4" />
            {t("settings.home.back")}
          </Link>
          <SectionPanel section={active} />
        </motion.div>
      </>
    );
  }

  if (!wide) {
    return (
      <motion.div
        animate="show"
        className={cn(PAGE_COLUMN, "space-y-5")}
        data-testid="settings-home"
        initial="hidden"
        variants={container}
      >
        {header}
        <nav aria-label={t("settings.home.sectionsLabel")}>
          <ul className={cn(LIST_CARD, "divide-y")}>
            {sections.map((section) => (
              <li key={section.value}>
                <Link
                  className="flex min-h-[68px] items-center gap-3 px-4 py-3 transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-v3-brand-ring sm:gap-4 sm:px-5"
                  data-testid={`settings-link-${section.value}`}
                  hash={section.value}
                  to="."
                >
                  <span aria-hidden className={ICON_TILE}>
                    {section.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-semibold leading-6">
                      {section.label}
                    </span>
                    <span className="block text-sm text-v3-text-3">
                      {section.description}
                    </span>
                  </span>
                  <ChevronRight
                    aria-hidden
                    className="size-5 shrink-0 text-v3-text-3"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </motion.div>
    );
  }

  return (
    <motion.div
      animate="show"
      className={cn(PAGE_COLUMN, "max-w-[1120px] space-y-6")}
      data-testid="settings-home"
      initial="hidden"
      variants={container}
    >
      {header}
      <div className="grid grid-cols-[220px_minmax(0,1fr)] items-start gap-8 xl:grid-cols-[240px_minmax(0,1fr)]">
        <nav
          aria-label={t("settings.home.sectionsLabel")}
          className="sticky top-4"
        >
          <ul className="space-y-1">
            {sections.map((section) => (
              <li key={section.value}>
                <Link
                  // The router marks every hash link on /settings as the
                  // current page; only the open section is.
                  activeOptions={{ includeHash: true }}
                  aria-current={
                    section.value === active?.value ? "page" : undefined
                  }
                  className={NAV_ITEM}
                  data-testid={`settings-link-${section.value}`}
                  hash={section.value}
                  replace
                  to="."
                >
                  {section.icon}
                  {section.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        {active && <SectionPanel key={active.value} section={active} />}
      </div>
    </motion.div>
  );
};
